import { Object3D, Scene, Vector3 } from 'three';
import { IEnemy } from './IEnemy';
import { EnemyFighter } from './EnemyFighter';
import { EliteFighter } from './EliteFighter';
import { Turret } from './Turret';
import { Vessel } from './Vessel';
import { EnemyBulletSystem } from './EnemyBullet';
import { EnemyMissileSystem } from './EnemyMissile';
import { EnemyDef } from '../game/StageLoader';
import { Explosion } from '../effects/Explosion';

export class EnemyManager {
  private enemies: IEnemy[] = [];
  private bulletSystem: EnemyBulletSystem;
  readonly missileSystem: EnemyMissileSystem;
  private eliteFighters: EliteFighter[] = [];
  private turrets: Turret[] = [];
  private scene: Scene;
  private getHeightAt?: (x: number, z: number) => number;

  constructor(scene: Scene, defs: EnemyDef[], getHeightAt?: (x: number, z: number) => number) {
    this.scene = scene;
    this.getHeightAt = getHeightAt;
    this.bulletSystem = new EnemyBulletSystem(scene);
    this.missileSystem = new EnemyMissileSystem(scene);
    this.spawnDefs(defs);
  }

  /** EnemyDef配列からユニットを生成して追加 */
  spawnDefs(defs: EnemyDef[]): void {
    for (const def of defs) {
      const pos: [number, number, number] = [...def.pos];
      if (pos[1] < 0 && this.getHeightAt) {
        pos[1] = this.getHeightAt(pos[0], pos[2]);
      }

      if (def.type === 'fighter') {
        const f = new EnemyFighter(this.scene, pos, def.ai === 'aggressive');
        f.onFireBullet = (from, dir) => this.bulletSystem.fire(from, dir);
        this.enemies.push(f);
      } else if (def.type === 'elite') {
        const e = new EliteFighter(this.scene, pos, def.ai === 'aggressive');
        e.onFireBullet = (from, dir) => this.bulletSystem.fire(from, dir);
        e.onFireMissile = (from, dir) => this.missileSystem.fire(from, dir);
        this.eliteFighters.push(e);
        this.enemies.push(e);
      } else if (def.type === 'turret') {
        const t = new Turret(this.scene, pos);
        t.onFireBullet = (from, dir) => this.bulletSystem.fire(from, dir);
        t.onFireMissile = (from, dir) => this.missileSystem.fire(from, dir);
        this.turrets.push(t);
        this.enemies.push(t);
      } else if (def.type === 'vessel') {
        const v = new Vessel(this.scene, pos);
        v.onFireBullet = (from, dir) => this.bulletSystem.fire(from, dir);
        this.enemies.push(v);
      }
    }
  }

  setPlayerRef(player: Object3D): void {
    this.missileSystem.setPlayerRef(player);
  }

  /** @returns プレイヤーへの合計ダメージ（近接 + 弾丸ヒット + ミサイルヒット） */
  update(delta: number, playerObj: Object3D, _explosion: Explosion): number {
    const playerPos = playerObj.position;
    let totalDamage = 0;

    for (const enemy of this.enemies) {
      if (!enemy.isAlive) continue;
      if (enemy instanceof EliteFighter) {
        totalDamage += enemy.update(delta, playerPos);
      } else if (enemy instanceof EnemyFighter) {
        totalDamage += enemy.update(delta, playerPos);
      } else if (enemy instanceof Turret) {
        totalDamage += enemy.update(delta, playerPos);
      } else if (enemy instanceof Vessel) {
        totalDamage += enemy.update(delta, playerPos);
      }
    }

    totalDamage += this.bulletSystem.update(delta, playerPos);

    // 敵ミサイル更新 + プレイヤー当たり判定
    this.missileSystem.update(delta);
    const missileHit = this.missileSystem.checkPlayerHit(playerPos, 15);
    if (missileHit) {
      totalDamage += missileHit.damage;
    }

    return totalDamage;
  }

  /** いずれかの敵がプレイヤーをロックオン中か */
  isPlayerLockedByEnemy(): boolean {
    return this.eliteFighters.some(e => e.isAlive && e.lockingPlayer)
        || this.turrets.some(t => t.isAlive && t.lockingPlayer);
  }

  /** アクティブな敵ミサイル位置一覧 */
  getEnemyMissilePositions(): Vector3[] {
    return this.missileSystem.getActiveMissiles();
  }

  remove(enemy: IEnemy): void {
    const idx = this.enemies.indexOf(enemy);
    if (idx !== -1) this.enemies.splice(idx, 1);
  }

  getAliveEnemies(): IEnemy[] {
    return this.enemies.filter(e => e.isAlive);
  }
}
